/**
 * <product-description-toggle>
 *
 * Inline expand / collapse for the product description. The full description
 * stays in the DOM; per breakpoint it is clipped to the height of the Nth word
 * (data-mobile-words / data-desktop-words — empty = no truncation at that
 * breakpoint) with a fade mask and a "Read more" tertiary button. Expanding /
 * collapsing animates max-height. On collapse the viewport is returned to where
 * "Read more" originally sat so the reader keeps their place. The expanded
 * Collapse button is sticky on mobile (CSS) and static on desktop.
 */
class ProductDescriptionToggle extends HTMLElement {
  #content = null;
  #trigger = null;
  #label = null;
  #wordCount = 250;
  #moreLabel = 'Read more';
  #lessLabel = 'Collapse';
  #collapsedHeight = 0;
  #active = false;
  #anchorOffset = 0;
  #anchorId = '';
  #fromAnchor = false;
  #mql = null;
  #resizeRaf = 0;
  #scrollRaf = 0;
  #atcObserver = null;

  connectedCallback() {
    this.#content = this.querySelector('[data-content]');
    this.#trigger = this.querySelector('[data-trigger]');
    this.#label = this.querySelector('[data-label]');
    if (!this.#content || !this.#trigger || !this.#label) return;

    this.#moreLabel = this.#trigger.dataset.moreLabel || this.#moreLabel;
    this.#lessLabel = this.#trigger.dataset.lessLabel || this.#lessLabel;
    this.#anchorId = this.dataset.anchorId || '';

    this.#trigger.addEventListener('click', this.#onClick);
    this.#mql = window.matchMedia('(max-width: 749px)');
    this.#mql.addEventListener('change', this.#onBreakpoint);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    // A "Read more" link in another block (the truncated teaser) points at this
    // description's anchor. Expand in place when it's followed so the reader
    // lands on the open copy, not a second collapsed toggle.
    if (this.#anchorId) {
      document.addEventListener('click', this.#onAnchorLinkClick);
      window.addEventListener('hashchange', this.#onHashChange);
    }
    this.#observeStickyAtc();

    // Measure after the first layout/paint so widths and fonts are settled.
    requestAnimationFrame(() => this.#setup());
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onClick);
    this.#mql?.removeEventListener('change', this.#onBreakpoint);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('click', this.#onAnchorLinkClick);
    window.removeEventListener('hashchange', this.#onHashChange);
    this.#atcObserver?.disconnect();
    cancelAnimationFrame(this.#resizeRaf);
    cancelAnimationFrame(this.#scrollRaf);
  }

  /**
   * The sticky add-to-cart bar's height is content-driven, so the expanded
   * Collapse button needs to clear its actual height (not a guess). Mirror it
   * into --pdt-atc-height for the CSS clearance rule, keeping it in sync as the
   * bar reflows (e.g. on a variant change).
   */
  #observeStickyAtc() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar || typeof ResizeObserver === 'undefined') return;
    this.#atcObserver = new ResizeObserver(() => {
      this.style.setProperty('--pdt-atc-height', `${bar.offsetHeight}px`);
    });
    this.#atcObserver.observe(bar);
  }

  get #expanded() {
    return this.dataset.state === 'expanded';
  }

  #onClick = () => {
    if (this.#expanded) this.#collapse(true);
    else this.#expand();
  };

  // Followed a link (from anywhere on the page) pointing at this description's
  // anchor — e.g. the truncated teaser's "Read more". Let the browser's native
  // jump run; the anchor sits above the toggle content, so expanding grows the
  // copy beneath it without disturbing the scroll position.
  #onAnchorLinkClick = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || link.getAttribute('href') !== `#${this.#anchorId}`) return;
    this.#expandFromAnchor();
  };

  #onHashChange = () => {
    if (window.location.hash === `#${this.#anchorId}`) this.#expandFromAnchor();
  };

  // Only meaningful while this breakpoint is actually truncating (toggle active
  // and collapsed); a no-op otherwise, so the full-text breakpoints are unaffected.
  #expandFromAnchor() {
    if (this.#active && !this.#expanded) this.#expand(true);
  }

  /**
   * The word-count cutoff for the current breakpoint, or null when this
   * breakpoint shouldn't truncate (toggle off / no value).
   * @returns {number | null}
   */
  #wordCountForBreakpoint() {
    const raw = this.#mql.matches ? this.dataset.mobileWords : this.dataset.desktopWords;
    const count = parseInt(raw || '', 10);
    return Number.isFinite(count) && count > 0 ? count : null;
  }

  #onBreakpoint = () => {
    // Re-evaluate from scratch: the active breakpoint, its word count, and
    // whether it truncates at all may all have changed.
    this.#teardown();
    requestAnimationFrame(() => this.#setup());
  };

  // While the expanded Collapse button is sticky (mobile), track whether it's
  // floating over more text ("stuck") or has landed at the bottom of the
  // description ("resting"). The CSS shows the border only while stuck.
  #onScroll = () => {
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = 0;
      this.#updateStuckState();
    });
  };

  #updateStuckState() {
    if (!this.#expanded || !this.#mql.matches) {
      this.removeAttribute('data-stuck');
      return;
    }
    // Stuck while the button's bottom edge sits above the host's bottom edge
    // (content still scrolls beneath it). They meet once it rests at the end.
    const buttonBottom = this.#trigger.getBoundingClientRect().bottom;
    const hostBottom = this.getBoundingClientRect().bottom;
    this.dataset.stuck = Math.ceil(buttonBottom) < Math.floor(hostBottom) ? 'true' : 'false';
  }

  #onResize = () => {
    // Only re-measure while collapsed — width changes the word→height mapping.
    if (!this.#active || this.#expanded) return;
    cancelAnimationFrame(this.#resizeRaf);
    this.#resizeRaf = requestAnimationFrame(() => {
      const words = this.#wordCountForBreakpoint();
      if (words == null) {
        this.#teardown();
        return;
      }
      this.#wordCount = words;
      this.#content.classList.remove('is-clamped');
      this.#content.style.maxHeight = '';
      const height = this.#measureCollapseHeight();
      if (height == null) {
        this.#teardown();
        return;
      }
      this.#collapsedHeight = height;
      this.#content.style.maxHeight = `${height}px`;
      this.#content.classList.add('is-clamped');
    });
  };

  #setup() {
    if (this.#expanded) return;

    const words = this.#wordCountForBreakpoint();
    if (words == null) {
      this.#teardown();
      return;
    }
    this.#wordCount = words;

    const height = this.#measureCollapseHeight();
    if (height == null) {
      this.#teardown();
      return;
    }

    this.#collapsedHeight = height;
    this.#active = true;
    // none → px is not animatable, so the initial clamp applies instantly.
    this.#content.classList.add('is-collapsible', 'is-clamped');
    this.#content.style.maxHeight = `${height}px`;
    this.#label.textContent = this.#moreLabel;
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#trigger.hidden = false;
    this.dataset.state = 'collapsed';

    // Deep-linked straight to this description (or jumped before JS was ready) —
    // open it rather than presenting a collapsed toggle.
    if (this.#anchorId && window.location.hash === `#${this.#anchorId}`) {
      requestAnimationFrame(() => this.#expandFromAnchor());
    }
  }

  #teardown() {
    this.#active = false;
    this.#content.classList.remove('is-collapsible', 'is-clamped');
    this.#content.style.maxHeight = '';
    this.#trigger.hidden = true;
    this.dataset.state = '';
    this.removeAttribute('data-stuck');
  }

  /**
   * Returns the pixel height at which the Nth word's line ends, measured from
   * the content's top. Returns null when there are fewer than the cutoff words
   * (or the cutoff lands so near the end that clipping isn't worth it).
   * @returns {number | null}
   */
  #measureCollapseHeight() {
    const content = this.#content;
    const previousMax = content.style.maxHeight;
    content.style.maxHeight = '';

    const fullHeight = content.scrollHeight;
    const contentTop = content.getBoundingClientRect().top;
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);

    let count = 0;
    let result = null;
    let node;
    while ((node = walker.nextNode())) {
      const wordRegex = /\S+/g;
      let match;
      while ((match = wordRegex.exec(node.nodeValue))) {
        count += 1;
        if (count >= this.#wordCount) {
          const range = document.createRange();
          range.setStart(node, match.index);
          range.setEnd(node, match.index + match[0].length);
          result = Math.ceil(range.getBoundingClientRect().bottom - contentTop);
          break;
        }
      }
      if (result != null) break;
    }

    content.style.maxHeight = previousMax;

    if (result == null || fullHeight - result < 48) return null;
    return result;
  }

  #expand(fromAnchor = false) {
    const content = this.#content;
    // Track whether this open came from a jump to the anchor (teaser "Read more")
    // vs. the toggle's own in-place "Read more" — collapse restores differently.
    this.#fromAnchor = fromAnchor;
    // Remember where the trigger sits now so collapse can restore the view.
    this.#anchorOffset = this.getBoundingClientRect().top;

    this.dataset.state = 'expanded';
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#label.textContent = this.#lessLabel;
    content.classList.remove('is-clamped');
    content.style.maxHeight = `${content.scrollHeight}px`;

    const onEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'max-height') return;
      // Drop the fixed height so the description can reflow freely while open.
      content.style.maxHeight = 'none';
      content.removeEventListener('transitionend', onEnd);
      this.#updateStuckState();
    };
    content.addEventListener('transitionend', onEnd);

    // Seed the stuck state immediately (the transitionend pass refines it once
    // the content has finished growing).
    requestAnimationFrame(() => this.#updateStuckState());
  }

  #collapse(restoreViewport) {
    const content = this.#content;

    if (restoreViewport) {
      const anchor = this.#fromAnchor && this.#anchorId ? document.getElementById(this.#anchorId) : null;
      if (anchor) {
        // Opened by jumping to the anchor (teaser "Read more"): collapse back to
        // the top of the full description, not the pre-jump spot up at the teaser.
        anchor.scrollIntoView();
      } else {
        // Opened in place (its own "Read more"): keep the reader where they were.
        const target = this.getBoundingClientRect().top + window.scrollY - this.#anchorOffset;
        window.scrollTo(0, Math.max(0, target));
      }
    }

    // Establish an explicit start height (none → px doesn't animate).
    content.style.maxHeight = `${content.scrollHeight}px`;
    content.classList.add('is-clamped');
    void content.offsetHeight; // force reflow
    requestAnimationFrame(() => {
      content.style.maxHeight = `${this.#collapsedHeight}px`;
    });

    this.dataset.state = 'collapsed';
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#label.textContent = this.#moreLabel;
    this.#updateStuckState();
  }
}

if (!customElements.get('product-description-toggle')) {
  customElements.define('product-description-toggle', ProductDescriptionToggle);
}
